import axios from 'axios';
import { API_BASE_URL } from '../config';
import { logger } from '../utils/logger';
import { getCachedResponse, setCachedResponse, clearCache } from '../utils/apiCache';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Required for cookies to work in cross-origin requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store token in localStorage as fallback when cookies are blocked
const TOKEN_STORAGE_KEY = 'auth_token';

// Add request interceptor to include token in Authorization header if cookies aren't working
apiClient.interceptors.request.use(
  (config) => {
    // Check if we have a token in localStorage (fallback for when cookies are blocked)
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    
    // Always add Authorization header if we have token (cookies might not work)
    // This ensures authentication works even when cookies are blocked
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      logger.debug('🔑 Using token from localStorage for:', config.url);
    }
    
    // Check cache for GET requests (except auth endpoints, search queries, and seller-specific requests)
    // Don't cache search queries or seller requests as they should always return fresh results
    if (config.method === 'get' && !config.url.includes('/auth/')) {
      const hasSearchParam = config.params && (config.params.search || config.params.query);
      const hasSellerParam = config.params && config.params.seller === 'true';
      if (!hasSearchParam && !hasSellerParam) {
        const cached = getCachedResponse(config.url, config.params);
        if (cached) {
          logger.debug('📦 Using cached response for:', config.url);
          // Return cached response by rejecting with a special error that we'll handle
          return Promise.reject({ cached, data: cached });
        }
      }
    }
    
    // Log cookie info for debugging (development only)
    logger.debug('API Request:', {
      url: config.url,
      method: config.method,
      withCredentials: config.withCredentials,
      hasAuthHeader: !!config.headers.Authorization,
      hasTokenInStorage: !!token,
    });
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to log 401 errors and check Set-Cookie headers
apiClient.interceptors.response.use(
  (response) => {
    // Cache GET responses (except auth endpoints, search queries, and seller-specific requests)
    // Don't cache search queries or seller requests as they should always return fresh results
    if (response.config.method === 'get' && !response.config.url.includes('/auth/')) {
      const hasSearchParam = response.config.params && (response.config.params.search || response.config.params.query);
      const hasSellerParam = response.config.params && response.config.params.seller === 'true';
      if (!hasSearchParam && !hasSellerParam) {
        setCachedResponse(response.config.url, response.config.params, response.data);
      }
    }
    
    // Check if response has Set-Cookie header (especially important for login)
    const setCookieHeader = response.headers['set-cookie'];
    if (setCookieHeader) {
      logger.debug('✅ Response Set-Cookie header found:', {
        url: response.config?.url,
        cookieCount: Array.isArray(setCookieHeader) ? setCookieHeader.length : 1,
      });
    } else if (response.config?.url?.includes('/auth/login')) {
      logger.warn('⚠️ Login response has NO Set-Cookie header!');
    }
    return response;
  },
  (error) => {
    // Handle cached responses
    if (error.cached) {
      return Promise.resolve({ data: error.data, fromCache: true });
    }
    
    if (error.response?.status === 401) {
      logger.error('❌ 401 Unauthorized for:', {
        url: error.config?.url,
        method: error.config?.method,
        message: error.response?.data?.message || 'Unauthorized',
      });
    }
    return Promise.reject(error);
  }
);

const extractData = (response) => response?.data?.data;

export const fetchCurrentUser = async () => {
  try {
    const { data } = await apiClient.get('/auth/me');
    const user = data?.data?.user || null;
    
    logger.debug('fetchCurrentUser response:', { 
      user: user ? { id: user._id, username: user.username, role: user.role } : null, 
      hasUser: !!user,
    });
    
    return user;
  } catch (error) {
    // Silently handle 401 errors for unauthenticated users
    if (error.response?.status === 401) {
      logger.debug('fetchCurrentUser: 401 Unauthorized - user not logged in');
      return null;
    }
    
    logger.error('fetchCurrentUser error:', {
      message: error.message,
      status: error.response?.status,
    });
    
    throw error;
  }
};

export const login = async (email, password) => {
  const response = await apiClient.post('/auth/login', { email, password });
  return extractData(response);
};

export const register = async (userData) => {
  const response = await apiClient.post('/auth/register', userData);
  return extractData(response);
};

export const logout = async () => {
  await apiClient.post('/auth/logout');
};

export const fetchSellerFarm = async () => {
  const { data } = await apiClient.get('/farms/me/profile');
  return data?.data;
};

export const fetchTopCategoryProducts = async () => {
  const { data } = await apiClient.get('/products/top/category');
  return data?.data;
};

export const createFarmProfile = async (payload) => {
  const response = await apiClient.post('/farms', payload);
  return extractData(response);
};

export const updateFarmProfile = async (id, payload) => {
  const response = await apiClient.put(`/farms/${id}`, payload);
  return extractData(response);
};

export const fetchProducts = async (params = {}) => {
  const response = await apiClient.get('/products', { params });
  return {
    products: response?.data?.data || [],
    meta: response?.data?.meta || {},
  };
};

// Helper to clear cache after mutations
const clearProductCache = () => {
  clearCache('/products');
  clearCache('/products/top');
  clearCache('/products/top/category');
  // Also clear any cached product-related data
  clearCache('/api/products');
};

export const fetchProductById = async (id) => {
  const response = await apiClient.get(`/products/${id}`);
  return extractData(response);
};

// Admin APIs
export const fetchUsers = async (params = {}) => {
  const response = await apiClient.get('/users', { params });
  return response?.data?.data || [];
};

export const updateUserStatus = async (id, payload) => {
  const response = await apiClient.patch(`/users/${id}/status`, payload);
  return extractData(response);
};

export const fetchFarms = async (params = {}) => {
  const response = await apiClient.get('/farms', { params });
  return response?.data?.data || [];
};

export const approveFarm = async (id, isApproved) => {
  const response = await apiClient.patch(`/farms/${id}/approval`, { isApproved });
  return extractData(response);
};

export const fetchPendingProducts = async () => {
  const response = await apiClient.get('/products', { params: { status: 'pending' } });
  return response?.data?.data || [];
};

export const approveProduct = async (id, isApproved, isPublished = null) => {
  const payload = { isApproved };
  if (isPublished !== null) payload.isPublished = isPublished;
  const response = await apiClient.patch(`/products/${id}/approval`, payload);
  clearProductCache(); // Clear cache after product update
  return extractData(response);
};

export const uploadProductImage = async (file) => {
  const formData = new FormData();
  formData.append('image', file);
  const response = await apiClient.post('/upload/products', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return extractData(response);
};

export const uploadMultipleProductImages = async (files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('images', file));
  const response = await apiClient.post('/upload/products/multiple', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return extractData(response);
};

export const createOrder = async (payload) => {
  const response = await apiClient.post('/orders', payload);
  return extractData(response);
};

export const fetchBuyerOrders = async () => {
  const response = await apiClient.get('/orders/me');
  return response?.data?.data || [];
};

export const fetchSellerOrders = async () => {
  const response = await apiClient.get('/orders/seller');
  return response?.data?.data || [];
};

export const fetchOrdersAdmin = async (params = {}) => {
  const response = await apiClient.get('/orders', { params });
  return response?.data?.data || [];
};

export const updateOrderStatus = async (id, status) => {
  const response = await apiClient.patch(`/orders/${id}/status`, { status });
  return extractData(response);
};

// Payment APIs
export const initiateMpesaPayment = async (orderId, phoneNumber) => {
  const response = await apiClient.post(`/payments/orders/${orderId}/initiate`, { phoneNumber });
  return extractData(response);
};

export const queryPaymentStatus = async (orderId) => {
  const response = await apiClient.get(`/payments/orders/${orderId}/status`);
  return extractData(response);
};


